import { useState } from "react";
import { Collapsible } from "radix-ui";
import useLocalStorageState from "use-local-storage-state";
import Button from "@/components/bricks/button/Button";
import CollapseIcon from "@/components/icons/CollapseIcon";
import FilterIcon from "@/components/icons/FilterIcon";
import type { ReviewSubjectType, ReviewThreadStatus } from "@/api/cle-api";
import ReviewThreadAssignmentFilter, {
  type ReviewThreadAssignment,
} from "../pages/efteruddannelse/filters/ReviewThreadAssignmentFilter";
import ReviewThreadOwnerFilter from "../pages/efteruddannelse/filters/ReviewThreadOwnerFilter";
import ReviewThreadStatusFilter from "../pages/efteruddannelse/filters/ReviewThreadStatusFilter";
import ReviewThreadSubjectTypeFilter from "../pages/efteruddannelse/filters/ReviewThreadSubjectTypeFilter";

export interface ReviewThreadFilterState extends ReviewThreadAssignment {
  statuses: ReviewThreadStatus[];
  subjectTypes: ReviewSubjectType[];
  ownerUserId?: string;
  ownerDisplayName?: string;
}

interface FiltersEfteruddannelseProps {
  filters: ReviewThreadFilterState;
  onStatusToggle: (status: ReviewThreadStatus, checked: boolean) => void;
  onSubjectTypeToggle: (
    subjectType: ReviewSubjectType,
    checked: boolean,
  ) => void;
  onOwnerChange: (owner?: { id: string; name: string }) => void;
  onAssignmentChange: (assignment: ReviewThreadAssignment) => void;
  onReset: () => void;
}

export default function FiltersEfteruddannelse({
  filters,
  onStatusToggle,
  onSubjectTypeToggle,
  onOwnerChange,
  onAssignmentChange,
  onReset,
}: FiltersEfteruddannelseProps) {
  const [collapsibleOpen, setCollapsibleOpen] = useLocalStorageState(
    "efteruddannelse-filters-sidebar-open",
    { defaultValue: true },
  );
  const [, setContainer] = useState<null | HTMLDivElement>(null);

  return (
    <Collapsible.Root
      open={collapsibleOpen}
      onOpenChange={setCollapsibleOpen}
      className="c-collapsible bg-light"
    >
      <Collapsible.Trigger className="c-collapsible-trigger" asChild>
        <Button
          icon
          transparent
          color="secondary"
          aria-label={collapsibleOpen ? "Skjul panel" : "Åbn filtre"}
          className={collapsibleOpen ? "tooltip-end" : ""}
          onClick={() => {}}
        >
          {collapsibleOpen ? <CollapseIcon /> : <FilterIcon />}
        </Button>
      </Collapsible.Trigger>
      <Collapsible.Content className="h-100">
        <div className="l-sidebar" ref={setContainer}>
          <header className="navbar navbar--sub bg-light">
            <div>
              <span className="me-2">
                <FilterIcon />
              </span>
              <h2 className="fw-medium">Filtre</h2>
            </div>
          </header>
          <div className="l-sidebar__scrollable pb-3">
            <ReviewThreadStatusFilter
              statuses={filters.statuses}
              onStatusToggle={onStatusToggle}
            />
            <ReviewThreadSubjectTypeFilter
              subjectTypes={filters.subjectTypes}
              onSubjectTypeToggle={onSubjectTypeToggle}
            />
            <ReviewThreadOwnerFilter
              ownerUserId={filters.ownerUserId}
              ownerDisplayName={filters.ownerDisplayName}
              onOwnerChange={onOwnerChange}
            />
            <ReviewThreadAssignmentFilter
              assignedTo={filters.assignedTo}
              assignedAdminEmail={filters.assignedAdminEmail}
              assignedAdminDisplayName={filters.assignedAdminDisplayName}
              onAssignmentChange={onAssignmentChange}
            />
          </div>
          <footer className="navbar navbar--sub navbar--footer bg-light position-static">
            <button
              type="button"
              className="btn btn-secondary btn--transparent"
              onClick={onReset}
            >
              Nulstil
            </button>
          </footer>
        </div>
      </Collapsible.Content>
    </Collapsible.Root>
  );
}
